import { GRID_TYPE } from '@eventbrite/eds-media-card-content';
import {
    CONSUMER_CARD_DEFAULTS,
    DismissableEventCard,
    EventCardPromoCode,
    EventCardShareAction,
    FormattedEvent,
    parseFollowSignal,
} from '@eventbrite/event-renderer';
import { GenericLazyString } from '@eventbrite/i18n';
import classNames from 'classnames';
import React, { useState } from 'react';
import DiscoverVerticalBrowseHierarchyEventCard from '../../../../components/DiscoverEventCard/Vertical/DiscoverVerticalBrowseHierarchyEventCard';
import { GA_HOME_FEED_CATEGORY } from '../../../../constants/analytics';
import { FOR_YOU } from '../../../../constants/tabConfig';
import { ShareOptions } from '../../../../types/Share';
import { getLocationInfo } from '../../../../utils/venue';
import { HEAP_LOCATION_STRING } from '../../constants/analytics';
import { InterestsSelectorContainer } from '../../containers/InterestsSelectorContainer';
import { ConfirmationNotification } from '../ConfirmationNotification/ConfirmationNotification';
import './ConsumerCards.scss';
import './ConsumerOnboardingCard.scss';

const _insertOnboardingCard = ({
    cardsComponent,
}: {
    cardsComponent: React.ReactElement[];
    eventCardContainerClassName?: string;
    isAuthenticated?: boolean;
    isInOddLayout?: boolean;
    onClickOnBoardingCard?: Function;
}): React.ReactElement[] => {
    const [card1, card2, card3, ...remainingCards] = cardsComponent;

    const cardsWithOnboarding = [card3];

    const _cardsComponent = [
        card1,
        card2,
        ...cardsWithOnboarding,
        ...remainingCards,
    ];

    return _cardsComponent;
};

interface ConsumerCardsProps {
    isMobile?: boolean;
    headerTitle?: JSX.Element;
    bucketKey?: string;
    bucketLabel?: string | GenericLazyString;
    onViewEvent?: Function;
    fetchUserSavedEvents: Function;
    bucketLayoutConfig?: { [key: string]: any };
    cardType?: string;
    eventsGroups?: EventGroup[];
    isAuthenticated?: boolean;
    isFreeTab?: boolean;
    isInOddLayout?: boolean;
    onClickOnBoardingCard?: Function;
    shouldShowOnboardingCard?: boolean;
    isFirstBucket?: boolean;
    enableInterestsSelector?: boolean;
    shareOptions: ShareOptions;
    tabKey: string;
    locale: string;
    onRemoveEvent?(eventId: string): void;
}

interface Event extends FormattedEvent {
    hideStartDate?: boolean;
    numFollowers?: number;
    venueName?: string;
    venueAddress?: string;
}

interface EventGroup {
    events: Event[];
    containerClasses: string;
}

interface UtmOptions {
    'utm-campaign': string;
    'utm-content': string;
    'utm-medium': string;
    'utm-term': string;
    'utm-source': string;
    'utm-experiment'?: string;
}

const ConsumerCards = ({
    //TODO: update api to handle passing custom tracking labels
    isMobile,
    headerTitle,
    bucketKey,
    bucketLabel,
    onViewEvent,
    bucketLayoutConfig,
    cardType,
    isAuthenticated,
    isFreeTab,
    isInOddLayout,
    onClickOnBoardingCard,
    shouldShowOnboardingCard,
    enableInterestsSelector,
    eventsGroups = [],
    shareOptions,
    onRemoveEvent,
    tabKey,
    locale,
}: ConsumerCardsProps) => {
    const [statefulEventGroups, setStatefulEventGroups] = useState(
        eventsGroups as EventGroup[],
    );
    const bucketClassNames =
        (bucketLayoutConfig && bucketLayoutConfig.bucketClassNames) || '';

    let cardsComponent: React.ReactElement[] = [];

    const utmOptions: UtmOptions = {
        'utm-campaign': 'social',
        'utm-content': 'attendeeshare',
        'utm-medium': 'discovery',
        'utm-term': 'listing',
        'utm-source': 'cp',
    };

    const [showConfirmationNotification, setShowConfirmationNotification] =
        useState(false);

    const handleRemoveEvent = (eventId: string) => {
        const updatedEventGroups = [
            {
                containerClasses: statefulEventGroups?.[0]?.containerClasses,
                events: statefulEventGroups?.[0]?.events?.filter(
                    (event) => event.id !== eventId,
                ),
            },
        ];
        setStatefulEventGroups(updatedEventGroups);
        onRemoveEvent?.(eventId);
        setShowConfirmationNotification(true);
        setTimeout(() => setShowConfirmationNotification(false), 3000);
    };

    //Flatten eventGroup into a single array of event cards
    for (const eventGroup of statefulEventGroups) {
        const { events, containerClasses } = eventGroup;

        const eventCardsComponents = events.map(
            ({
                id,
                minPrice,
                isFree,
                isSoldOut,
                startDate,
                hideStartDate,
                numFollowers,
                venueName,
                venueAddress,
                ...cardProps
            }) => {
                //Once query client properly set up this can be removed
                const signal = parseFollowSignal(numFollowers || 0);
                return (
                    <div
                        className={`${containerClasses}`}
                        key={id}
                        data-testid={`home-event-card-${id}`}
                    >
                        {isAuthenticated ? (
                            <DismissableEventCard
                                {...CONSUMER_CARD_DEFAULTS}
                                {...signal}
                                bucketLabel={bucketLabel?.toString()}
                                bucketKey={bucketKey}
                                isMobile={isMobile}
                                containerSpacingClasses=""
                                eventData={{
                                    ...cardProps,
                                    id: id || '',
                                    isFree: (!isFreeTab && isFree) || false,
                                    isSoldOut: isSoldOut,
                                    startDate: hideStartDate ? '' : startDate,
                                    organizerFollowerCount: numFollowers,
                                }}
                                onClick={onViewEvent?.bind(
                                    null,
                                    id,
                                    bucketKey,
                                    numFollowers,
                                )}
                                locationInfo={getLocationInfo(
                                    venueName,
                                    venueAddress,
                                )}
                                minPrice={minPrice}
                                gaCategory={GA_HOME_FEED_CATEGORY}
                                hidePriceDescription={isFreeTab}
                                isAuthenticated={isAuthenticated || false}
                                affCode={
                                    tabKey === FOR_YOU
                                        ? 'ebdssbehomepeforyou'
                                        : 'ehometext'
                                }
                                isLazyImage={true}
                                heapLocationString={HEAP_LOCATION_STRING}
                                shareOptions={{
                                    ...shareOptions,
                                    eventId: id,
                                    eventName: cardProps.name,
                                    eventUrl: cardProps.url,
                                    utmOptions,
                                    trackingCategory: 'eventcard',
                                }}
                                showFewerEventsIcon={
                                    tabKey === FOR_YOU ? true : false
                                }
                                onRemoveEvent={handleRemoveEvent}
                                promoCodeLabel={
                                    <EventCardPromoCode
                                        openDiscount={cardProps.openDiscount}
                                        locale={locale}
                                    />
                                }
                            />
                        ) : (
                            <DiscoverVerticalBrowseHierarchyEventCard
                                locale={locale || 'en_US'}
                                statsigLocationString={HEAP_LOCATION_STRING}
                                isAuthenticated={false}
                                event={{
                                    ...cardProps,
                                    id: id || '',
                                    isFree: (!isFreeTab && isFree) || false,
                                    isSoldOut: isSoldOut,
                                    startDate: hideStartDate ? '' : startDate,
                                    minPrice: minPrice,
                                    organizerFollowerCount: numFollowers,
                                }}
                                isLazyImage
                                hidePriceDescription={isFreeTab}
                                affCode={'ehometext'}
                                data-testid="home-event-card"
                                moreActions={
                                    <EventCardShareAction
                                        shareOptions={{
                                            ...shareOptions,
                                            eventId: id,
                                            eventName: cardProps.name,
                                            eventUrl: cardProps.url,
                                            utmOptions,
                                            trackingCategory: 'eventcard',
                                        }}
                                    />
                                }
                            />
                        )}
                    </div>
                );
            },
        );

        cardsComponent = [...cardsComponent, ...eventCardsComponents];
    }
    /*
        shouldShowOnboardingCard: Property of bucket defined in tabConfig
    */
    if (shouldShowOnboardingCard) {
        const eventCardContainerClassName = classNames(
            'feed__card-cell',
            'eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1',
            {
                'eds-g-cell-sm-6-12 eds-g-cell-mn-4-12 eds-g-cell-lg-3-12':
                    cardType === GRID_TYPE,
            },
        );

        cardsComponent = _insertOnboardingCard({
            cardsComponent,
            eventCardContainerClassName,
            isAuthenticated,
            isInOddLayout,
            onClickOnBoardingCard,
        });
    }

    return (
        <div className={bucketClassNames}>
            {showConfirmationNotification ? <ConfirmationNotification /> : null}
            {headerTitle}
            <div className="feed-events-bucket__content__cards-container">
                {cardsComponent}
            </div>

            {enableInterestsSelector && (
                <InterestsSelectorContainer
                    key="interests-selector"
                    isAuthenticated={isAuthenticated}
                />
            )}
        </div>
    );
};

export default ConsumerCards;
